import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, Users, FileText, Loader2, RefreshCw,
  Rocket, Calculator, Download, Calendar, Target, ArrowUp, ArrowDown, Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, change, changeLabel, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600'
  };
  
  return (
    <div className="bg-white rounded-xl border p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {change !== undefined && (
            <div className="flex items-center gap-1 mt-2">
              {change >= 0 ? (
                <ArrowUp className="text-emerald-600" size={14} />
              ) : (
                <ArrowDown className="text-rose-600" size={14} />
              )}
              <span className={`text-sm ${change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {Math.abs(change)}%
              </span>
              <span className="text-xs text-gray-500">{changeLabel}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
};

// Template colors for charts
const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const UsageAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  const fetchAnalytics = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    const token = secureStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const response = await authedFetch(`${API_URL}/api/solution-finder/analytics/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        setError(null);
      } else {
        throw new Error('Failed to fetch analytics');
      }
    } catch (err) {
      setError('Failed to load analytics data');
      logger.error(err);
    }
    
    setLoading(false);
    setRefreshing(false);
  }, [navigate]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  // Transform template popularity for pie chart
  const templatePieData = analytics?.template_popularity?.map((t, i) => ({
    name: t.template_name?.replace(/\s+/g, ' ').substring(0, 20) || t.template_id,
    value: t.uses,
    color: CHART_COLORS[i % CHART_COLORS.length]
  })) || [];
  
  // Transform daily usage for line chart
  const dailyData = analytics?.daily_usage?.map(d => ({
    date: d.date?.substring(5) || d.date, // MM-DD format
    uses: d.count
  })) || [];
  
  // Get KPI names for bar chart
  const KPI_NAMES = {
    engagement: 'Employee Engagement',
    productivity: 'Productivity',
    quality: 'Quality',
    safety: 'Safety',
    mobility: 'Mobility',
    customer_satisfaction: 'Customer Satisfaction'
  };
  
  const kpiBarData = analytics?.kpi_distribution?.map(k => ({
    name: KPI_NAMES[k.kpi_id] || k.kpi_id,
    count: k.count
  })) || [];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }
  
  const __pageTheme = getLightSectionTheme('Navy');
  const usageHeroActions = (
    <button
      onClick={() => fetchAnalytics(true)}
      disabled={refreshing}
      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 border text-slate-700 rounded-lg hover:bg-white disabled:opacity-50 text-sm"
      data-testid="usage-refresh-btn"
    >
      <RefreshCw className={`${refreshing ? 'animate-spin' : ''}`} size={16} />
      Refresh
    </button>
  );
  const usageHeroStats = [
    {
      label: 'Template Uses',
      value: analytics?.summary?.total_template_uses || 0,
      testId: 'usage-stat-template-uses',
      whyPanel: {
        label: 'Template Uses',
        value: analytics?.summary?.total_template_uses || 0,
        description: 'Total times any Solution Finder template has been chosen to seed a new scenario, across the whole tenant lifetime.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A growing counter signals the templates are providing value. Lifetime metric — pair with "Last 7 Days" for a momentum read.' },
          { label: 'What to do', value: '', hint: 'Open Solution Finder and check which templates are most-used; double down by adding sister templates around them.' },
        ],
        links: [{ label: 'Open Solution Finder', path: '/solution-finder' }],
      },
    },
    {
      label: 'Last 7 Days',
      value: analytics?.summary?.recent_template_uses_7d || 0,
      testId: 'usage-stat-recent',
      whyPanel: {
        label: 'Template Uses (Last 7 Days)',
        value: analytics?.summary?.recent_template_uses_7d || 0,
        description: 'Template uses in the past week — the freshest pulse on whether your Solution Finder library is being adopted right now.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Compare this against your weekly target. A drop while total grows means engagement is plateauing — refresh the templates or promote new ones.' },
          { label: 'What to do', value: '', hint: (analytics?.summary?.recent_template_uses_7d || 0) === 0
              ? 'No template uses this week. Send a release email featuring a new template or add an in-app banner to surface fresh content.'
              : 'Healthy weekly cadence. Keep an eye on which days spike — that is when your audience is most engaged.' },
        ],
      },
    },
    {
      label: 'ROI Calculations',
      value: analytics?.summary?.roi_calculations || 0,
      testId: 'usage-stat-roi-calcs',
      whyPanel: {
        label: 'ROI Calculations Run',
        value: analytics?.summary?.roi_calculations || 0,
        description: 'How many times users have advanced past the assumptions step and asked the engine to compute the ROI multiple. The deepest signal of "real" engagement.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A 10:1 ratio of Template Uses to ROI Calculations is normal — most users explore, fewer commit. Watch for this ratio shifting unfavourably.' },
          { label: 'What to do', value: '', hint: 'Compare against Cases Exported below — calculations without exports often signal a UX drop-off in the export step.' },
        ],
      },
    },
    {
      label: 'Cases Exported',
      value: analytics?.summary?.business_cases_exported || 0,
      testId: 'usage-stat-exported',
      whyPanel: {
        label: 'Business Cases Exported',
        value: analytics?.summary?.business_cases_exported || 0,
        description: 'Number of times a user clicked Export on a saved scenario (PDF / Word / PPT). A direct proxy for "this case got delivered to a stakeholder."',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Cases Exported ÷ ROI Calculations is the closest analogue of a conversion rate inside LIE. Healthy is 30%+.' },
          { label: 'What to do', value: '', hint: 'If exports lag, the issue is usually scenario polish — surface the polish step earlier in the wizard, or add a tear-sheet preview before the export click.' },
        ],
      },
    },
    {
      label: 'Active Users',
      value: analytics?.summary?.unique_users_30d || 0,
      testId: 'usage-stat-active-users',
      whyPanel: {
        label: 'Monthly Active Users',
        value: analytics?.summary?.unique_users_30d || 0,
        description: 'Distinct users who took any modelling, analytics, or import action in the last 30 days. Standard MAU definition.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'For a paid SaaS, MAU÷licensed_seats is your stickiness ratio. Healthy = > 50% of seats active monthly.' },
          { label: 'What to do', value: '', hint: 'If MAU lags, look at the segment-level breakdown below to see whether one team carries the engagement vs everyone using a little.' },
        ],
      },
    },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Usage Analytics"
        description="Track Solution Finder and template usage patterns. Monitor adoption and identify popular features."
        theme={__pageTheme}
        icon={Activity}
        actions={usageHeroActions}
        stats={usageHeroStats}
        testId="usage-analytics-hero"
      />
    )}>
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 md:px-8 lg:px-12 py-8">

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700">
            {error}
          </div>
        )}
        
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Template Popularity Pie Chart */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Popularity</h3>
            {templatePieData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={templatePieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {templatePieData.map((entry, index) => (
                        <Cell key={entry.name || index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Rocket className="mx-auto mb-2 text-gray-400" size={32} />
                  <p>No template usage data yet</p>
                </div>
              </div>
            )}
          </div>
          
          {/* KPI Distribution Bar Chart */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">KPI Category Distribution</h3>
            {kpiBarData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kpiBarData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Target className="mx-auto mb-2 text-gray-400" size={32} />
                  <p>No KPI data yet</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Daily Usage Trend */}
        <div className="bg-white rounded-xl border p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Template Usage (Last 30 Days)</h3>
          {dailyData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="uses" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <TrendingUp className="mx-auto mb-2 text-gray-400" size={32} />
                <p>No daily usage data yet</p>
                <p className="text-sm mt-1">Start using templates to see trends</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Template Leaderboard */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Leaderboard</h3>
          {analytics?.template_popularity?.length > 0 ? (
            <div className="space-y-3">
              {analytics.template_popularity.map((template, idx) => (
                <div 
                  key={template.template_id}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                    idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{template.template_name}</p>
                    <p className="text-xs text-gray-500">{template.template_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">{template.uses}</p>
                    <p className="text-xs text-gray-500">uses</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="mx-auto mb-2 text-gray-400" size={32} />
              <p>No template usage data yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </PageShell>
  );
};

export default UsageAnalyticsPage;
